import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface ConversationRow {
  id: string;
  user_id: string;
  title: string;
  dify_conversation_id: string | null;
  created_at: string;
  updated_at: string;
}

const LIST_KEY = ["conversations", "list"] as const;

function makeTitle(text: string) {
  const t = text.replace(/\s+/g, " ").trim();
  if (!t) return "Nuova conversazione";
  return t.length > 50 ? t.slice(0, 47).trimEnd() + "…" : t;
}

export function useConversations() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const enabled = !!user;

  const query = useQuery({
    queryKey: LIST_KEY,
    enabled,
    queryFn: async (): Promise<ConversationRow[]> => {
      const { data, error } = await supabase
        .from("conversations")
        .select("id, user_id, title, dify_conversation_id, created_at, updated_at")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data as ConversationRow[] | null) ?? [];
    },
  });

  const items = query.data ?? [];

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: LIST_KEY });
  }, [qc]);

  const create = useCallback(
    async (title = "Nuova conversazione"): Promise<ConversationRow> => {
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("conversations")
        .insert({ user_id: user.id, title })
        .select("id, user_id, title, dify_conversation_id, created_at, updated_at")
        .single();
      if (error) throw error;
      invalidate();
      return data as ConversationRow;
    },
    [user, invalidate],
  );

  const get = useCallback(
    async (id: string): Promise<ConversationRow | null> => {
      const { data, error } = await supabase
        .from("conversations")
        .select("id, user_id, title, dify_conversation_id, created_at, updated_at")
        .eq("id", id)
        .maybeSingle();
      if (error) return null;
      return (data as ConversationRow | null) ?? null;
    },
    [],
  );

  const updateTitle = useCallback(
    async (id: string, title: string) => {
      const { error } = await supabase
        .from("conversations")
        .update({ title, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (!error) invalidate();
    },
    [invalidate],
  );

  const updateDifyConversationId = useCallback(
    async (id: string, difyId: string) => {
      const { error } = await supabase
        .from("conversations")
        .update({ dify_conversation_id: difyId, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (!error) invalidate();
    },
    [invalidate],
  );

  const touch = useCallback(
    async (id: string) => {
      const { error } = await supabase
        .from("conversations")
        .update({ updated_at: new Date().toISOString() })
        .eq("id", id);
      if (!error) invalidate();
    },
    [invalidate],
  );

  /**
   * Auto-titola se il titolo è ancora "Nuova conversazione".
   * Da chiamare dopo il primo messaggio utente.
   */
  const maybeAutoTitle = useCallback(
    async (id: string, currentTitle: string, firstUserContent: string) => {
      if (currentTitle !== "Nuova conversazione") return;
      const next = makeTitle(firstUserContent);
      if (!next || next === currentTitle) return;
      await updateTitle(id, next);
    },
    [updateTitle],
  );

  const remove = useCallback(
    async (id: string) => {
      const { error } = await supabase.from("conversations").delete().eq("id", id);
      if (!error) invalidate();
    },
    [invalidate],
  );

  const clearAll = useCallback(async () => {
    if (!user) return;
    const { error } = await supabase.from("conversations").delete().eq("user_id", user.id);
    if (!error) invalidate();
  }, [user, invalidate]);

  // Senza rete la lettura resta in pausa e l'elenco e' vuoto: non vuol dire
  // «nessuna conversazione» (audit 04/10, lo Storico offline diceva cosi').
  const senzaDati = query.data === undefined;
  const storicoNonDisponibile: "offline" | "errore" | null = !senzaDati
    ? null
    : query.fetchStatus === "paused"
      ? "offline"
      : query.isError
        ? "errore"
        : null;

  return {
    items,
    isLoading: query.isLoading,
    storicoNonDisponibile,
    ricaricaStorico: () => void query.refetch(),
    create,
    get,
    updateTitle,
    updateDifyConversationId,
    touch,
    maybeAutoTitle,
    remove,
    clearAll,
  };
}

/** Cosa dire al posto dell'elenco quando lo storico non si e' potuto leggere. */
export function testoStoricoNonDisponibile(stato: "offline" | "errore"): string {
  return stato === "offline"
    ? "Sei offline: le conversazioni si vedono quando torna la rete."
    : "Non riesco a caricare le conversazioni in questo momento.";
}
